import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";

const STAR_COUNT = 380;

const makeStar = () => ({
  x: (Math.random() - 0.5) * 2.2,
  y: (Math.random() - 0.5) * 2.2,
  z: 0.05 + Math.random() * 0.95,
  tint: Math.random(),
});

const starColor = (tint, a) =>
  tint > 0.82
    ? `rgba(178,138,255,${a})`
    : tint > 0.62
      ? `rgba(140,255,225,${a})`
      : `rgba(255,255,255,${a})`;

export const SpaceIntro = () => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState("dark");
  const [gone, setGone] = useState(false);
  const canvasRef = useRef(null);
  const speedTarget = useRef(0.003);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const endIntro = (delay) => {
    setPhase("fade");
    timersRef.current.push(
      setTimeout(() => {
        setGone(true);
        document.documentElement.style.overflow = "";
      }, delay),
    );
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setGone(true);
      return;
    }
    document.documentElement.style.overflow = "hidden";
    const t = (fn, ms) => timersRef.current.push(setTimeout(fn, ms));
    t(() => {
      setPhase("approach");
      speedTarget.current = 0.005;
    }, 900);
    t(() => {
      setPhase("hold");
      speedTarget.current = 0.0035;
    }, 2900);
    t(() => {
      setPhase("warp");
      speedTarget.current = 0.095;
    }, 5600);
    t(() => endIntro(400), 6900);
    return () => {
      clearTimers();
      document.documentElement.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    clearTimers();
    setPhase("warp");
    speedTarget.current = 0.12;
    endIntro(1100);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const stars = Array.from({ length: STAR_COUNT }, makeStar);
    let last = performance.now();
    let alpha = 0;
    const speed = { current: 0.003 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const loop = (now) => {
      const dt = Math.min(now - last, 50);
      last = now;
      speed.current += (speedTarget.current - speed.current) * 0.075;
      alpha = Math.min(1, alpha + dt / 900);

      const w = window.innerWidth;
      const h = window.innerHeight;
      const cx = w / 2;
      const cy = h / 2;
      const scale = Math.min(w, h) * 0.5;
      ctx.clearRect(0, 0, w, h);

      const sp = speed.current * dt;
      const warping = speed.current > 0.02;

      for (const s of stars) {
        const pz = s.z;
        s.z -= sp;
        if (s.z <= 0.02) {
          Object.assign(s, makeStar(), { z: 1 + Math.random() * 0.3 });
          continue;
        }
        const sx = cx + (s.x / s.z) * scale;
        const sy = cy + (s.y / s.z) * scale;
        const px = cx + (s.x / pz) * scale;
        const py = cy + (s.y / pz) * scale;
        if (sx < -60 || sx > w + 60 || sy < -60 || sy > h + 60) continue;

        const depth = 1 - s.z;
        const size = Math.max(0.4, depth * 2.2);
        const a = Math.min(1, depth * 1.4) * alpha;

        if (warping) {
          const stretch = Math.min(1 + speed.current * 55, 9);
          ctx.strokeStyle = starColor(s.tint, a * 0.9);
          ctx.lineWidth = size * 0.9;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(sx + (sx - px) * (stretch - 1), sy + (sy - py) * (stretch - 1));
          ctx.stroke();
        } else {
          ctx.fillStyle = starColor(s.tint, a);
          ctx.beginPath();
          ctx.arc(sx, sy, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  if (gone) return null;

  const textAnim = {
    dark: { scale: 0.012, opacity: 0 },
    approach: { scale: 1, opacity: 1 },
    hold: { scale: 1.02, opacity: 1 },
    warp: { scale: 42, opacity: 0 },
    fade: { scale: 42, opacity: 0 },
  };
  const textTransition = {
    dark: { duration: 0.3 },
    approach: { duration: 1.9, ease: [0.62, 0.02, 0.86, 0.48] },
    hold: { duration: 0.6, ease: "easeOut" },
    warp: { duration: 1.15, ease: [0.7, 0, 0.95, 0.35] },
    fade: { duration: 0.3 },
  };

  return (
    <AnimatePresence>
      <motion.div
        key="intro-overlay"
        data-testid="space-intro"
        initial={{ opacity: 1 }}
        animate={{ opacity: phase === "fade" ? 0 : 1 }}
        transition={{ duration: 0.45, ease: "easeInOut" }}
        className="fixed inset-0 z-[100] overflow-hidden bg-[#020206]"
      >
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

        {/* cinematic vignette */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 52%, rgba(0,0,0,0.75) 100%)",
          }}
        />

        {/* arrival shockwave ring at the money shot */}
        {phase === "hold" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <motion.span
              initial={{ scale: 0.4, opacity: 0.9 }}
              animate={{ scale: 2.1, opacity: 0 }}
              transition={{ duration: 1.3, ease: "easeOut" }}
              className="block h-56 w-[74vw] max-w-3xl rounded-[100%] border border-[#14F195]/40"
            />
          </div>
        )}

        {/* SOLMOO — the centerpiece */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.h1
            initial={false}
            animate={textAnim[phase]}
            transition={textTransition[phase]}
            className={`font-display text-center text-[clamp(2.1rem,8.5vw,7rem)] font-extrabold tracking-tight text-flow ${
              phase === "hold" ? "intro-glow" : ""
            }`}
          >
            SOLMOO
          </motion.h1>
        </div>

        {/* warp ignition flash */}
        {phase === "warp" && (
          <motion.div
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="pointer-events-none absolute inset-0 bg-[#EDE9FF]"
          />
        )}

        <motion.button
          data-testid="intro-skip-btn"
          onClick={skip}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="absolute bottom-6 right-6 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold tracking-wide text-gray-400 backdrop-blur transition-colors hover:border-white/30 hover:text-white"
        >
          {t("intro.skip")}
        </motion.button>
      </motion.div>
    </AnimatePresence>
  );
};
